import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import WorktimeCard from './worktimeCard';
import { CalendarWrapper, EditWrapper } from '../../assets/styles/components/worktime.styled';
import { IWorkTime } from '../../interface/workTimes';
import { useEffect, useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { InputTextarea, Text } from '../Ant';
import { useTranslation } from 'react-i18next';

const localizer = dayjsLocalizer(dayjs);

interface IDayWorkTimeView {
  workTimes: IWorkTime[];
  selectedDate: Date;
  createWorkTime?: (workTime: IWorkTime) => void;
}
const CurrentTimeDivider = () => {
  const styles: React.CSSProperties = {
    position: 'absolute',
    height: '1px',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
  };

  const time = new Date();

  return (
    <div style={styles}>
      <span className="current-time">{dayjs(time).format('HH:mm')}</span>
    </div>
  );
};
const CustomEvent = ({
  event,
  handleSaveInline,
}: {
  event: IWorkTime;
  handleSaveInline: (id: string, newTitle: string) => void;
}) => {
  const { t } = useTranslation();
  const [editTitle, setEditTitle] = useState(event?.subject || '');
  if (event?.isEditingForDragableCreate) {
    return (
      <EditWrapper className="h-full relative">
        <InputTextarea
          data-testid="worktime-edit-input"
          autoFocus
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          placeholder={t('common:addSubject')}
          className="event-input"
          autoSize={false}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSaveInline(event.id, editTitle);
            }
          }}
        />
        <Text
          data-testid="worktime-edit-input-note"
          $fontsize="11px"
          $color="description"
          $lineheight="12px"
          className="event-input-note"
        >
          {t('worktime:hitEnterToSaveSubject')}
        </Text>
      </EditWrapper>
    );
  }

  return (
    <WorktimeCard
      title={event.subject}
      color={event?.workTimeType?.color}
      description={event?.matter && `${event?.matter?.matterNumber} - ${event?.matter?.subject}`}
      id={event.id}
      key={event.id}
      headingclass="flex-shrink-0"
    />
  );
};

const WorktimeCalendar = ({ selectedDate, workTimes, createWorkTime }: IDayWorkTimeView) => {
  const [eventsData, setEventsData] = useState<IWorkTime[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const lastScrollTimeRef = useRef(0);

  /**
   * Handles auto-scrolling during calendar selection to keep the selection range visible.
   * Calculates the pixel position of the selection and scrolls the calendar if necessary.
   * Also sets the isSelecting state to true when selection starts.
   * Throttled to prevent excessive scrolling during rapid selection changes.
   */
  const handleSelectionAutoScroll = (range: { start: Date; end: Date }): boolean => {
    const container = document.querySelector('.rbc-time-content') as HTMLElement;
    if (!container) {
      if (!isSelecting) setIsSelecting(true);
      return true;
    }

    // Throttle scroll updates to every 150ms
    const now = Date.now();
    const timeSinceLastScroll = now - lastScrollTimeRef.current;

    if (timeSinceLastScroll < 150) {
      if (!isSelecting) setIsSelecting(true);
      return true;
    }

    lastScrollTimeRef.current = now;

    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const endHour = range.end.getHours() + range.end.getMinutes() / 60;
    const startHour = range.start.getHours() + range.start.getMinutes() / 60;
    const pixelPerHour = 70; // based on min-height
    const endPixel = endHour * pixelPerHour;
    const startPixel = startHour * pixelPerHour;
    if (endPixel > scrollTop + clientHeight - 100) {
      container.scrollTo({ top: endPixel - clientHeight + 100, behavior: 'smooth' });
    } else if (startPixel < scrollTop + 100) {
      container.scrollTo({ top: startPixel - 100, behavior: 'smooth' });
    }

    if (!isSelecting) setIsSelecting(true);
    return true;
  };
  const handleSaveInline = (id: string, newTitle: string) => {
    setEventsData((prev) =>
      prev.map((event) =>
        event.id === id
          ? {
              ...event,
              title: newTitle,
              subject: newTitle,
              isEditingForDragableCreate: false,
            }
          : event,
      ),
    );
    const newWorkTime = eventsData.find((event) => event.id === id);
    createWorkTime?.({
      ...newWorkTime,
      subject: newTitle,
    } as IWorkTime);
  };

  const handleSelect = ({ start, end }: { start: Date; end: Date }) => {
    setEventsData((prev) => {
      const updated = prev.filter((event) => !event.isEditingForDragableCreate);
      return [
        ...updated,
        {
          id: crypto.randomUUID(),
          start,
          end,
          startTime: start,
          endTime: end,
          title: '',
          subject: '',
          isEditingForDragableCreate: true,
        } as unknown as IWorkTime,
      ];
    });
  };

  useEffect(() => {
    setEventsData(
      (workTimes ?? [])?.map((workTime) => ({
        title: workTime.subject,
        start: dayjs(workTime.startTime).toDate(),
        end: dayjs(workTime.endTime).toDate(),
        ...workTime,
      })),
    );
  }, [workTimes]);
  useEffect(() => {
    const isToday = dayjs(selectedDate).isSame(dayjs(), 'day');
    if (!isToday) return;

    const timeoutId = setTimeout(() => {
      const element = document.querySelector('.rbc-current-time-indicator');
      const container = document.querySelector('.rbc-time-content');

      if (element && container) {
        const elementOffsetTop = (element as HTMLElement).offsetTop;
        const containerHeight = container.clientHeight;

        container.scrollTo({
          top: elementOffsetTop - containerHeight / 2,
          behavior: 'smooth',
        });
        const timeNode = document.createElement('div');
        element.appendChild(timeNode);

        const root = createRoot(timeNode);
        root.render(<CurrentTimeDivider />);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedDate]);
  const renderCustomEvents = (props: { event: IWorkTime }) => (
    <CustomEvent {...props} handleSaveInline={handleSaveInline} />
  );
  return (
    <CalendarWrapper>
      <Calendar<IWorkTime>
        localizer={localizer}
        events={eventsData}
        selectable
        defaultView="day"
        dayLayoutAlgorithm="no-overlap"
        views={['day']}
        date={selectedDate}
        step={60}
        timeslots={1}
        toolbar={false}
        length={2}
        onSelecting={handleSelectionAutoScroll}
        onSelectSlot={(slotInfo) => {
          // Get bounding box of selection for floating input
          handleSelect({ ...slotInfo });
          setIsSelecting(false);
        }}
        components={{
          event: renderCustomEvents,
        }}
        formats={{
          timeGutterFormat: (date: Date) => dayjs(date).format('HH:mm'),
        }}
      />
    </CalendarWrapper>
  );
};

export default WorktimeCalendar;
